/* ==========================================================================
   ANEMOIA PAINT — /pages/nfc-paint
   Windows XP era MS Paint, touch-first. Canvas 2D only, no dependencies.

   Layer model
     colorCv  — everything the user paints (fills, strokes, shapes, text)
     inkCv    — the traced line art, composited ON TOP, never painted into
     display  — #pt-canvas, = colorCv then inkCv
   Keeping ink separate is what makes the colouring page work; see
   anemoia-trace.js for why the drawing must not double as the fill barrier.

   Undo is patch-based (bbox ImageData diffs), not full-canvas snapshots —
   a phone will not hold 10 x 4MB of history.
   ========================================================================== */

import { tracePhoto } from './anemoia-trace.js';

const $ = (id) => document.getElementById(id);

/* Classic MS Paint palette, authored as the two rows you actually see. */
const PALETTE_ROWS = [
  ['#000000', '#808080', '#800000', '#808000', '#008000', '#008080', '#000080',
   '#800080', '#808040', '#004040', '#0080ff', '#004080', '#8000ff', '#804000'],
  ['#ffffff', '#c0c0c0', '#ff0000', '#ffff00', '#00ff00', '#00ffff', '#0000ff',
   '#ff00ff', '#ffff80', '#00ff80', '#80ffff', '#8080ff', '#ff0080', '#ff8040'],
];

/* --- Tool icons. Drawn on a 16x16 grid with crisp edges, like the originals. */
const I = (paths) =>
  `<svg viewBox="0 0 16 16" shape-rendering="crispEdges" fill="none" stroke="#000" stroke-width="1">${paths}</svg>`;

const TOOLS = [
  { id: 'select-free', name: 'Free-Form Select',
    icon: I('<path d="M3 12c0-4 2-8 5-9 3 0 5 3 4 6-1 2-4 2-5 4" stroke-dasharray="2 1.5"/>') },
  { id: 'select', name: 'Select',
    icon: I('<rect x="2.5" y="3.5" width="11" height="9" stroke-dasharray="2 1.5"/>') },
  { id: 'eraser', name: 'Eraser',
    icon: I('<path d="M2.5 11.5l5-5 5 5-3 3h-4z" fill="#fff"/><path d="M7.5 6.5l3-3 5 5-3 3"/>') },
  { id: 'fill', name: 'Fill With Colour',
    icon: I('<path d="M3.5 8.5l5-5 5 5-5 5z" fill="#fff"/><path d="M12 11c1.5 2 2.5 3 1 4s-2.5-2-1-4z" fill="#000"/>') },
  { id: 'picker', name: 'Pick Colour',
    icon: I('<path d="M2.5 13.5l1-3 7-7 2 2-7 7z" fill="#fff"/><path d="M10.5 2.5l3 3"/>') },
  { id: 'magnifier', name: 'Magnifier',
    icon: I('<circle cx="7" cy="7" r="4"/><path d="M10 10l4 4"/>') },
  { id: 'pencil', name: 'Pencil',
    icon: I('<path d="M2.5 13.5l1-3 8-8 2 2-8 8z" fill="#fff"/><path d="M3.5 10.5l2 2"/>') },
  { id: 'brush', name: 'Brush',
    icon: I('<path d="M4 12c-1 1-2 1-2 1s0-1 1-2l6-6 1 1z" fill="#000"/><path d="M9 5l3-3 2 2-3 3z" fill="#fff"/>') },
  { id: 'airbrush', name: 'Airbrush',
    icon: I('<path d="M6.5 13.5v-5h3v5z" fill="#fff"/><circle cx="11" cy="4" r=".6" fill="#000" stroke="none"/><circle cx="13" cy="6" r=".6" fill="#000" stroke="none"/><circle cx="12" cy="2" r=".6" fill="#000" stroke="none"/><path d="M8 8.5l3-3"/>') },
  { id: 'text', name: 'Text',
    icon: I('<path d="M4 4.5h8M8 4.5v8M6 12.5h4" stroke-width="1.5"/>') },
  { id: 'line', name: 'Line',
    icon: I('<path d="M2.5 13.5l11-11"/>') },
  { id: 'curve', name: 'Curve',
    icon: I('<path d="M2.5 12.5c3-9 8-9 11-2"/>') },
  { id: 'rect', name: 'Rectangle',
    icon: I('<rect x="2.5" y="4.5" width="11" height="8"/>') },
  { id: 'polygon', name: 'Polygon',
    icon: I('<path d="M3.5 12.5l2-8 7 2-3 6z"/>') },
  { id: 'ellipse', name: 'Ellipse',
    icon: I('<ellipse cx="8" cy="8.5" rx="5.5" ry="4.5"/>') },
  { id: 'roundrect', name: 'Rounded Rectangle',
    icon: I('<rect x="2.5" y="4.5" width="11" height="8" rx="2.5"/>') },
];

/*
 * Reward. Same single offer and the same lightly-obfuscated code as ANEMOIA FC
 * (templates/page.nfc.liquid PRIZE) — every NFC tag must land on one consistent payoff,
 * and the tags only reach people who have already bought, so a shared static
 * code is fine: any redemption is a repeat sale.
 */
const _p = (s) => atob(s.split('').reverse().join(''));
const PRIZE = { line: 'YOU UNLOCKED $10 OFF YOUR NEXT ORDER', code: _p('=QWYlJnQ') /* Bread */ };

const FREEHAND = new Set(['pencil', 'brush', 'eraser', 'airbrush']);
const SHAPE = new Set(['line', 'rect', 'ellipse', 'roundrect']);
const HAS_FILLMODE = new Set(['rect', 'ellipse', 'roundrect', 'polygon']);

/* ========================================================================== */
class Paint {
  constructor() {
    this.canvas = $('pt-canvas');
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.wrap = $('pt-canvaswrap');
    this.stage = $('pt-canvasstage');

    this.color = { fg: '#000000', bg: '#ffffff' };
    this.tool = 'fill';
    this.size = 2;
    this.fillMode = 'outline';         // outline | filled | both
    this.textSize = 32;

    this.view = { scale: 1, tx: 0, ty: 0 };
    this.undoStack = [];
    this.redoStack = [];
    this.MAX_UNDO = 16;

    this.trace = null;                 // { labels, index, regionCount, means, ink }
    this.srcImageData = null;          // kept so Detail can re-trace without re-picking the file
    this.detail = 0.55;                // tuned by eye on the cap photo; see harness/sweep.mjs
    this.userDrawn = null;             // Uint8Array: pixels the customer painted by hand
    this.pointers = new Map();
    this.stroke = null;
    this.pending = null;               // multi-step tools (curve/polygon)

    this.buildToolbox();
    this.buildPalette();
    this.bindMenus();
    this.bindCanvas();
    this.bindDialogs();
    this.bindReward();
    this.setTool('fill');
    this.newCanvas(640, 480);
    this.runBoot();
  }

  /* ------------------------------------------------------------------ boot */
  /*
   * BIOS -> XP splash -> Luna welcome -> desktop. The markup is already on
   * screen (static in the template); this only advances phases and tears down.
   */
  async runBoot() {
    const boot = $('pt-boot');
    if (!boot) return;

    const skip = () => {
      if (this.bootDone) return;
      this.bootDone = true;
      boot.classList.add('is-gone');
      setTimeout(() => { boot.hidden = true; }, 520);
      show($('pt-welcome'));
    };
    boot.addEventListener('pointerdown', skip);
    document.addEventListener('keydown', function once(e) {
      if (e.key === 'Escape' || e.key === ' ' || e.key === 'Enter') {
        document.removeEventListener('keydown', once);
        skip();
      }
    });

    // ?nointro=1 (or the harness flag) drops straight to the app
    const params = new URLSearchParams(location.search);
    if (params.has('nointro') || window.__PT_NOBOOT) { skip(); return; }

    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    await wait(1500);
    if (this.bootDone) return;
    $('pt-boot-bios').hidden = true;
    $('pt-boot-xp').hidden = false;
    await wait(2600);
    if (this.bootDone) return;
    $('pt-boot-xp').hidden = true;
    $('pt-boot-welcome').hidden = false;
    await wait(1200);
    skip();
  }

  /* ---------------------------------------------------------------- reward */
  bindReward() {
    $('pt-done').addEventListener('click', () => this.openReward());
    $('pt-reward-back').addEventListener('click', () => hide($('pt-reward')));
    $('pt-reward-save').addEventListener('click', () => this.save());
    $('pt-copy').addEventListener('click', () => this.copyCode());
  }

  /*
   * The reward is for making something, so Done stays disabled until at least
   * one operation has been committed. undoStack is the honest signal: endOp
   * only pushes when an operation actually changed pixels, and undoing back to
   * an empty canvas correctly locks it again.
   */
  hasArt() {
    return this.undoStack.length > 0;
  }

  updateDoneState() {
    const btn = $('pt-done');
    if (!btn) return;
    const ok = this.hasArt();
    btn.disabled = !ok;
    btn.title = ok ? 'Claim your reward' : 'Draw something first';
  }

  openReward() {
    if (!this.hasArt()) { this.message('Draw something first, then claim your reward.'); return; }
    $('pt-prize-line').textContent = PRIZE.line;
    $('pt-prize-code').textContent = PRIZE.code;
    $('pt-copy').textContent = 'COPY CODE';
    this.closeMenu();
    show($('pt-reward'));
  }

  async copyCode() {
    const btn = $('pt-copy');
    try {
      await navigator.clipboard.writeText(PRIZE.code);
      btn.textContent = 'COPIED ✓';
    } catch (_) {
      // clipboard API needs a secure context and can still be refused
      const ta = document.createElement('textarea');
      ta.value = PRIZE.code;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        btn.textContent = 'COPIED ✓';
      } catch (_e) {
        btn.textContent = PRIZE.code;   // last resort: show it to be typed
      }
      ta.remove();
    }
    setTimeout(() => { btn.textContent = 'COPY CODE'; }, 2500);
  }

  /* ---------------------------------------------------------------- layers */
  newCanvas(w, h) {
    this.w = w;
    this.h = h;
    this.canvas.width = w;
    this.canvas.height = h;

    this.colorCv = document.createElement('canvas');
    this.colorCv.width = w; this.colorCv.height = h;
    this.cctx = this.colorCv.getContext('2d', { willReadFrequently: true });
    this.cctx.fillStyle = '#ffffff';
    this.cctx.fillRect(0, 0, w, h);

    this.inkCv = document.createElement('canvas');
    this.inkCv.width = w; this.inkCv.height = h;
    this.ictx = this.inkCv.getContext('2d');

    // scratch holding the pre-operation image, so undo patches can be cut
    // from it once the affected bbox is known
    this.beforeCv = document.createElement('canvas');
    this.beforeCv.width = w; this.beforeCv.height = h;
    this.bctx = this.beforeCv.getContext('2d', { willReadFrequently: true });

    this.userDrawn = new Uint8Array(w * h);
    this.undoStack = [];
    this.redoStack = [];
    this.setStatus('size', `${w} x ${h}`);
    this.updateDoneState();
    this.fitToView();
    this.repaint();
  }

  repaint() {
    this.ctx.clearRect(0, 0, this.w, this.h);
    this.ctx.drawImage(this.colorCv, 0, 0);
    if (this.trace) this.ctx.drawImage(this.inkCv, 0, 0);
  }

  /* ------------------------------------------------------------------ view */
  fitToView() {
    const r = this.wrap.getBoundingClientRect();
    const pad = 16;
    const scale = Math.min((r.width - pad) / this.w, (r.height - pad) / this.h, 4);
    this.view.scale = Math.max(scale, 0.05);
    this.view.tx = (r.width - this.w * this.view.scale) / 2;
    this.view.ty = (r.height - this.h * this.view.scale) / 2;
    this.applyView();
  }

  applyView() {
    const { scale, tx, ty } = this.view;
    this.stage.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
  }

  zoomAt(clientX, clientY, factor) {
    const r = this.wrap.getBoundingClientRect();
    const px = clientX - r.left;
    const py = clientY - r.top;
    const next = Math.min(Math.max(this.view.scale * factor, 0.05), 16);
    const k = next / this.view.scale;
    this.view.tx = px - (px - this.view.tx) * k;
    this.view.ty = py - (py - this.view.ty) * k;
    this.view.scale = next;
    this.applyView();
  }

  toCanvas(clientX, clientY) {
    const r = this.wrap.getBoundingClientRect();
    return {
      x: (clientX - r.left - this.view.tx) / this.view.scale,
      y: (clientY - r.top - this.view.ty) / this.view.scale,
    };
  }

  /* ------------------------------------------------------------------ undo */
  beginOp() {
    this.bctx.clearRect(0, 0, this.w, this.h);
    this.bctx.drawImage(this.colorCv, 0, 0);
    this.dirty = null;
  }

  markDirty(x0, y0, x1, y1, padPx = 0) {
    const p = padPx + 2;
    const r = {
      x0: Math.max(0, Math.floor(Math.min(x0, x1) - p)),
      y0: Math.max(0, Math.floor(Math.min(y0, y1) - p)),
      x1: Math.min(this.w, Math.ceil(Math.max(x0, x1) + p)),
      y1: Math.min(this.h, Math.ceil(Math.max(y0, y1) + p)),
    };
    if (!this.dirty) this.dirty = r;
    else {
      this.dirty.x0 = Math.min(this.dirty.x0, r.x0);
      this.dirty.y0 = Math.min(this.dirty.y0, r.y0);
      this.dirty.x1 = Math.max(this.dirty.x1, r.x1);
      this.dirty.y1 = Math.max(this.dirty.y1, r.y1);
    }
  }

  endOp({ handDrawn = false } = {}) {
    const d = this.dirty;
    if (!d || d.x1 <= d.x0 || d.y1 <= d.y0) return;
    const w = d.x1 - d.x0;
    const h = d.y1 - d.y0;

    this.undoStack.push({ x: d.x0, y: d.y0, data: this.bctx.getImageData(d.x0, d.y0, w, h) });
    if (this.undoStack.length > this.MAX_UNDO) this.undoStack.shift();
    this.redoStack = [];
    this.updateDoneState();

    /*
     * Record which pixels the customer actually changed by hand. The bucket
     * uses this to decide between region fill and classic flood fill, so it
     * has to be the real changed pixels, not the bounding box.
     */
    if (handDrawn && this.trace) {
      const before = this.bctx.getImageData(d.x0, d.y0, w, h).data;
      const after = this.cctx.getImageData(d.x0, d.y0, w, h).data;
      for (let i = 0, n = w * h; i < n; i++) {
        const o = i * 4;
        if (before[o] !== after[o] || before[o + 1] !== after[o + 1] ||
            before[o + 2] !== after[o + 2] || before[o + 3] !== after[o + 3]) {
          this.userDrawn[(d.y0 + ((i / w) | 0)) * this.w + (d.x0 + (i % w))] = 1;
        }
      }
    }
  }

  applyPatch(patch, into) {
    const { x, y, data } = patch;
    const inverse = { x, y, data: this.cctx.getImageData(x, y, data.width, data.height) };
    this.cctx.putImageData(data, x, y);
    into.push(inverse);
    this.repaint();
    this.updateDoneState();
  }

  undo() { const p = this.undoStack.pop(); if (p) this.applyPatch(p, this.redoStack); }
  redo() { const p = this.redoStack.pop(); if (p) this.applyPatch(p, this.undoStack); }

  /* ----------------------------------------------------------------- tools */
  setTool(id) {
    this.commitPending();
    this.tool = id;
    for (const b of this.toolbox.children) b.classList.toggle('is-active', b.dataset.tool === id);
    const t = TOOLS.find((x) => x.id === id);
    this.setStatus('msg', t ? t.name : '');
    this.buildToolOpts();
  }

  buildToolbox() {
    this.toolbox = $('pt-toolbox');
    this.toolbox.innerHTML = '';
    for (const t of TOOLS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pt-tool';
      b.dataset.tool = t.id;
      b.title = t.name;
      b.setAttribute('aria-label', t.name);
      b.innerHTML = t.icon;
      b.addEventListener('click', () => this.setTool(t.id));
      this.toolbox.appendChild(b);
    }
  }

  buildToolOpts() {
    const box = $('pt-toolopts');
    box.innerHTML = '';
    const add = (svg, active, onClick, label) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pt-opt' + (active ? ' is-active' : '');
      b.innerHTML = svg;
      b.title = label;
      b.setAttribute('aria-label', label);
      b.addEventListener('click', () => { onClick(); this.buildToolOpts(); });
      box.appendChild(b);
    };

    if (FREEHAND.has(this.tool) || this.tool === 'line') {
      for (const s of [1, 2, 4, 8]) {
        add(`<svg width="26" height="14"><rect x="4" y="${7 - s / 2}" width="18" height="${s}" fill="#000"/></svg>`,
          this.size === s, () => { this.size = s; }, `${s}px`);
      }
    }
    if (HAS_FILLMODE.has(this.tool)) {
      const modes = [
        ['outline', '<svg width="26" height="16"><rect x="4.5" y="3.5" width="17" height="9" fill="none" stroke="#000"/></svg>'],
        ['filled', '<svg width="26" height="16"><rect x="4.5" y="3.5" width="17" height="9" fill="#8080ff" stroke="#000"/></svg>'],
        ['both', '<svg width="26" height="16"><rect x="4.5" y="3.5" width="17" height="9" fill="#8080ff" stroke="#000" stroke-width="2"/></svg>'],
      ];
      for (const [m, svg] of modes) add(svg, this.fillMode === m, () => { this.fillMode = m; }, m);
    }
    if (this.tool === 'magnifier') {
      add('<svg width="26" height="16"><text x="13" y="12" font-size="11" text-anchor="middle">1x</text></svg>',
        false, () => this.fitToView(), 'Fit');
    }
  }

  buildPalette() {
    const pal = $('pt-palette');
    pal.innerHTML = '';
    // grid flows down-then-across, so interleave the two authored rows
    for (let c = 0; c < PALETTE_ROWS[0].length; c++) {
      for (const row of PALETTE_ROWS) {
        const col = row[c];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'pt-sw';
        b.style.background = col;
        b.dataset.color = col;
        b.title = col;
        b.setAttribute('aria-label', col);
        b.addEventListener('click', () => this.setColor(col));
        b.addEventListener('contextmenu', (e) => { e.preventDefault(); this.setColor(col, true); });
        pal.appendChild(b);
      }
    }
    this.syncSwatches();
  }

  setColor(col, secondary = false) {
    if (secondary) this.color.bg = col; else this.color.fg = col;
    this.syncSwatches();
  }

  syncSwatches() {
    $('pt-fg').style.background = this.color.fg;
    $('pt-bg').style.background = this.color.bg;
    for (const b of $('pt-palette').children) {
      b.classList.toggle('is-active', b.dataset.color === this.color.fg);
    }
  }

  setStatus(which, text) {
    const el = $(which === 'msg' ? 'pt-status-msg' : which === 'pos' ? 'pt-status-pos' : 'pt-status-size');
    if (el) el.textContent = text;
  }

  /* ------------------------------------------------------------- painting  */
  strokeStyleFor(button) {
    return button === 2 ? this.color.bg : this.color.fg;
  }

  drawDab(x, y, col) {
    const c = this.cctx;
    c.fillStyle = col;
    if (this.tool === 'pencil') {
      c.fillRect(Math.floor(x), Math.floor(y), 1, 1);
    } else {
      const s = this.tool === 'eraser' ? Math.max(this.size * 4, 8) : this.size * 2;
      c.beginPath();
      c.arc(x, y, s / 2, 0, Math.PI * 2);
      c.fill();
    }
  }

  drawSegment(x0, y0, x1, y1, col) {
    const c = this.cctx;
    if (this.tool === 'airbrush') {
      c.fillStyle = col;
      const r = this.size * 4;
      for (let i = 0; i < 12; i++) {
        const a = Math.random() * Math.PI * 2;
        const d = Math.sqrt(Math.random()) * r;
        c.fillRect(Math.round(x1 + Math.cos(a) * d), Math.round(y1 + Math.sin(a) * d), 1, 1);
      }
      this.markDirty(x1 - r, y1 - r, x1 + r, y1 + r);
      return;
    }
    if (this.tool === 'pencil') {
      // Bresenham keeps the 1px pencil honest at every zoom level
      let X0 = Math.floor(x0), Y0 = Math.floor(y0);
      const X1 = Math.floor(x1), Y1 = Math.floor(y1);
      const dx = Math.abs(X1 - X0), dy = -Math.abs(Y1 - Y0);
      const sx = X0 < X1 ? 1 : -1, sy = Y0 < Y1 ? 1 : -1;
      let err = dx + dy;
      c.fillStyle = col;
      for (;;) {
        c.fillRect(X0, Y0, 1, 1);
        if (X0 === X1 && Y0 === Y1) break;
        const e2 = 2 * err;
        if (e2 >= dy) { err += dy; X0 += sx; }
        if (e2 <= dx) { err += dx; Y0 += sy; }
      }
    } else {
      const s = this.tool === 'eraser' ? Math.max(this.size * 4, 8) : this.size * 2;
      c.strokeStyle = col;
      c.lineWidth = s;
      c.lineCap = 'round';
      c.lineJoin = 'round';
      c.beginPath();
      c.moveTo(x0, y0);
      c.lineTo(x1, y1);
      c.stroke();
    }
    this.markDirty(x0, y0, x1, y1, this.size * 4);
  }

  drawShape(kind, a, b, col, bgcol) {
    const c = this.cctx;
    const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
    const w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
    c.lineWidth = Math.max(this.size, 1);
    c.strokeStyle = col;
    c.fillStyle = bgcol;
    c.beginPath();
    if (kind === 'line') { c.moveTo(a.x, a.y); c.lineTo(b.x, b.y); }
    else if (kind === 'rect') c.rect(x, y, w, h);
    else if (kind === 'roundrect') {
      const r = Math.min(12, w / 2, h / 2);
      if (c.roundRect) c.roundRect(x, y, w, h, r); else c.rect(x, y, w, h);
    } else if (kind === 'ellipse') {
      c.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    }
    if (kind !== 'line' && this.fillMode !== 'outline') c.fill();
    if (kind === 'line' || this.fillMode !== 'filled') c.stroke();
    this.markDirty(a.x, a.y, b.x, b.y, this.size + 2);
  }

  /* ------------------------------------------------------------------ fill */
  bucket(x, y, col) {
    const px = Math.floor(x), py = Math.floor(y);
    if (px < 0 || py < 0 || px >= this.w || py >= this.h) return;
    const idx = py * this.w + px;

    // On a traced photo the region map owns the fill: leak-proof by design.
    // Once the customer has painted over a pixel by hand, that area is theirs,
    // so fall back to a classic flood fill bounded by their own strokes.
    if (this.trace && !this.userDrawn[idx]) this.fillRegion(idx, col);
    else this.floodFill(px, py, col);
  }

  fillRegion(idx, col) {
    const { labels, index } = this.trace;
    const region = labels[idx];
    const start = index.offset[region];
    const end = index.offset[region + 1];

    const img = this.cctx.getImageData(0, 0, this.w, this.h);
    const d = img.data;
    const [r, g, b] = hexToRgb(col);
    let x0 = this.w, y0 = this.h, x1 = 0, y1 = 0;
    for (let i = start; i < end; i++) {
      const p = index.pixels[i];
      const o = p * 4;
      d[o] = r; d[o + 1] = g; d[o + 2] = b; d[o + 3] = 255;
      const px = p % this.w, py = (p / this.w) | 0;
      if (px < x0) x0 = px; if (px > x1) x1 = px;
      if (py < y0) y0 = py; if (py > y1) y1 = py;
    }
    this.cctx.putImageData(img, 0, 0);
    this.markDirty(x0, y0, x1 + 1, y1 + 1);
  }

  /* Scanline flood fill — the classic bucket, used on blank canvases. */
  floodFill(sx, sy, col) {
    const img = this.cctx.getImageData(0, 0, this.w, this.h);
    const d = img.data;
    const w = this.w, h = this.h;
    const at = (x, y) => (y * w + x) * 4;
    const o0 = at(sx, sy);
    const tgt = [d[o0], d[o0 + 1], d[o0 + 2], d[o0 + 3]];
    const [r, g, b] = hexToRgb(col);
    if (tgt[0] === r && tgt[1] === g && tgt[2] === b && tgt[3] === 255) return;

    const match = (o) => {
      // tolerance keeps antialiased edges from leaving halos
      const dr = d[o] - tgt[0], dg = d[o + 1] - tgt[1], db = d[o + 2] - tgt[2], da = d[o + 3] - tgt[3];
      return dr * dr + dg * dg + db * db + da * da < 1200;
    };

    let x0 = w, y0 = h, x1 = 0, y1 = 0;
    const stack = [[sx, sy]];
    const seen = new Uint8Array(w * h);
    while (stack.length) {
      const [px, py] = stack.pop();
      let L = px;
      while (L > 0 && !seen[py * w + (L - 1)] && match(at(L - 1, py))) L--;
      let R = px;
      while (R < w - 1 && !seen[py * w + (R + 1)] && match(at(R + 1, py))) R++;
      for (let x = L; x <= R; x++) {
        const o = at(x, py);
        d[o] = r; d[o + 1] = g; d[o + 2] = b; d[o + 3] = 255;
        seen[py * w + x] = 1;
        if (py > 0 && !seen[(py - 1) * w + x] && match(at(x, py - 1))) stack.push([x, py - 1]);
        if (py < h - 1 && !seen[(py + 1) * w + x] && match(at(x, py + 1))) stack.push([x, py + 1]);
      }
      if (L < x0) x0 = L; if (R > x1) x1 = R;
      if (py < y0) y0 = py; if (py > y1) y1 = py;
    }
    this.cctx.putImageData(img, 0, 0);
    this.markDirty(x0, y0, x1 + 1, y1 + 1);
  }

  pickColor(x, y) {
    const d = this.ctx.getImageData(Math.floor(x), Math.floor(y), 1, 1).data;
    this.setColor(rgbToHex(d[0], d[1], d[2]));
  }

  /* --------------------------------------------------------------- pointer */
  bindCanvas() {
    const wrap = this.wrap;
    wrap.addEventListener('pointerdown', (e) => this.onDown(e));
    wrap.addEventListener('pointermove', (e) => this.onMove(e));
    wrap.addEventListener('pointerup', (e) => this.onUp(e));
    wrap.addEventListener('pointercancel', (e) => this.onUp(e));
    wrap.addEventListener('contextmenu', (e) => e.preventDefault());
    wrap.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.15 : 1 / 1.15);
    }, { passive: false });
    window.addEventListener('resize', () => this.fitToView());
  }

  onDown(e) {
    this.wrap.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, e);

    // second finger = pinch/pan; abandon whatever the first finger started
    if (this.pointers.size === 2) {
      this.cancelStroke();
      const [a, b] = [...this.pointers.values()];
      this.gesture = {
        dist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
        mx: (a.clientX + b.clientX) / 2,
        my: (a.clientY + b.clientY) / 2,
      };
      return;
    }
    if (this.pointers.size > 2) return;

    const p = this.toCanvas(e.clientX, e.clientY);
    const col = this.strokeStyleFor(e.button);

    if (this.tool === 'magnifier') {
      this.zoomAt(e.clientX, e.clientY, e.button === 2 ? 1 / 2 : 2);
      return;
    }
    if (this.tool === 'picker') { this.pickColor(p.x, p.y); return; }
    if (this.tool === 'text') { this.pendingText = p; this.openTextDialog(); return; }
    if (this.tool === 'curve' || this.tool === 'polygon') { this.multiStepDown(p, col); return; }

    this.beginOp();

    if (this.tool === 'fill') {
      this.bucket(p.x, p.y, col);
      this.endOp();
      this.repaint();
      return;
    }
    if (FREEHAND.has(this.tool)) {
      const c = this.tool === 'eraser' ? this.color.bg : col;
      this.stroke = { last: p, col: c, kind: 'freehand' };
      this.drawDab(p.x, p.y, c);
      this.markDirty(p.x, p.y, p.x, p.y, this.size * 4);
      this.repaint();
      return;
    }
    if (SHAPE.has(this.tool)) {
      this.stroke = { start: p, col, kind: 'shape' };
      return;
    }
    if (this.tool === 'select' || this.tool === 'select-free') {
      this.stroke = { start: p, col, kind: 'select', pts: [p] };
    }
  }

  onMove(e) {
    if (this.pointers.has(e.pointerId)) this.pointers.set(e.pointerId, e);

    if (this.pointers.size === 2 && this.gesture) {
      const [a, b] = [...this.pointers.values()];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const mx = (a.clientX + b.clientX) / 2;
      const my = (a.clientY + b.clientY) / 2;
      const r = this.wrap.getBoundingClientRect();
      // pan by midpoint delta, zoom by pinch ratio about the midpoint
      this.view.tx += mx - this.gesture.mx;
      this.view.ty += my - this.gesture.my;
      this.applyView();
      if (this.gesture.dist > 0) this.zoomAt(mx, my, dist / this.gesture.dist);
      this.gesture = { dist, mx, my };
      return;
    }

    const p = this.toCanvas(e.clientX, e.clientY);
    this.setStatus('pos', `${Math.floor(p.x)}, ${Math.floor(p.y)}`);
    if (!this.stroke) return;

    if (this.stroke.kind === 'freehand') {
      this.drawSegment(this.stroke.last.x, this.stroke.last.y, p.x, p.y, this.stroke.col);
      this.stroke.last = p;
      this.repaint();
    } else if (this.stroke.kind === 'shape') {
      this.previewShape(this.stroke.start, p, this.stroke.col);
    } else if (this.stroke.kind === 'select') {
      if (this.tool === 'select-free') this.stroke.pts.push(p);
      this.previewSelect(p);
    }
  }

  onUp(e) {
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.gesture = null;
    if (!this.stroke) return;

    const p = this.toCanvas(e.clientX, e.clientY);
    if (this.stroke.kind === 'freehand') {
      this.endOp({ handDrawn: true });
    } else if (this.stroke.kind === 'shape') {
      this.beginOp();
      this.drawShape(this.tool, this.stroke.start, p, this.stroke.col, this.color.bg);
      this.endOp({ handDrawn: true });
    } else if (this.stroke.kind === 'select') {
      this.finishSelect(p);
    }
    this.stroke = null;
    this.repaint();
  }

  cancelStroke() {
    if (!this.stroke) return;
    if (this.stroke.kind === 'freehand' && this.beforeCv) {
      // a stroke started by the finger that is now half of a pinch is noise
      this.cctx.clearRect(0, 0, this.w, this.h);
      this.cctx.drawImage(this.beforeCv, 0, 0);
      this.repaint();
    }
    this.stroke = null;
    this.pending = null;
  }

  /* Shape/selection previews composite onto the display canvas only, so the
     colour layer stays clean until the gesture is committed. */
  previewShape(a, b, col) {
    this.repaint();
    const c = this.ctx;
    c.save();
    c.lineWidth = Math.max(this.size, 1);
    c.strokeStyle = col;
    c.fillStyle = this.color.bg;
    c.beginPath();
    const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
    const w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
    if (this.tool === 'line') { c.moveTo(a.x, a.y); c.lineTo(b.x, b.y); }
    else if (this.tool === 'rect') c.rect(x, y, w, h);
    else if (this.tool === 'roundrect') { const r = Math.min(12, w / 2, h / 2); if (c.roundRect) c.roundRect(x, y, w, h, r); else c.rect(x, y, w, h); }
    else if (this.tool === 'ellipse') c.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    if (this.tool !== 'line' && this.fillMode !== 'outline') c.fill();
    if (this.tool === 'line' || this.fillMode !== 'filled') c.stroke();
    c.restore();
  }

  previewSelect(p) {
    this.repaint();
    const c = this.ctx;
    c.save();
    c.strokeStyle = '#000';
    c.lineWidth = 1 / this.view.scale;
    c.setLineDash([4 / this.view.scale, 3 / this.view.scale]);
    c.beginPath();
    if (this.tool === 'select') {
      const a = this.stroke.start;
      c.rect(Math.min(a.x, p.x), Math.min(a.y, p.y), Math.abs(p.x - a.x), Math.abs(p.y - a.y));
    } else {
      const pts = this.stroke.pts;
      c.moveTo(pts[0].x, pts[0].y);
      for (const q of pts) c.lineTo(q.x, q.y);
    }
    c.stroke();
    c.restore();
  }

  /* Selection is scoped to "clear this area" — the honest 80% of what people
     reach for it for, without a floating-clipboard state machine on a phone. */
  finishSelect(p) {
    const c = this.cctx;
    this.beginOp();
    c.save();
    c.beginPath();
    if (this.tool === 'select') {
      const a = this.stroke.start;
      c.rect(Math.min(a.x, p.x), Math.min(a.y, p.y), Math.abs(p.x - a.x), Math.abs(p.y - a.y));
      this.markDirty(a.x, a.y, p.x, p.y, 2);
    } else {
      const pts = this.stroke.pts;
      c.moveTo(pts[0].x, pts[0].y);
      for (const q of pts) c.lineTo(q.x, q.y);
      c.closePath();
      let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
      for (const q of pts) { x0 = Math.min(x0, q.x); y0 = Math.min(y0, q.y); x1 = Math.max(x1, q.x); y1 = Math.max(y1, q.y); }
      this.markDirty(x0, y0, x1, y1, 2);
    }
    c.clip();
    c.fillStyle = this.color.bg;
    c.fillRect(0, 0, this.w, this.h);
    c.restore();
    this.endOp({ handDrawn: true });
  }

  /* ---- multi-step tools (curve, polygon) --------------------------------- */
  multiStepDown(p, col) {
    if (!this.pending || this.pending.tool !== this.tool) {
      this.pending = { tool: this.tool, pts: [p], col };
      this.setStatus('msg', this.tool === 'curve'
        ? 'Curve: tap to set the end, then tap twice to bend it.'
        : 'Polygon: tap each corner, then tap the first corner to close.');
      return;
    }
    this.pending.pts.push(p);

    if (this.pending.tool === 'curve' && this.pending.pts.length === 4) { this.commitPending(); return; }
    if (this.pending.tool === 'polygon' && this.pending.pts.length > 2) {
      const first = this.pending.pts[0];
      if (Math.hypot(p.x - first.x, p.y - first.y) < 12 / this.view.scale) this.commitPending();
    }
    this.previewPending();
  }

  previewPending() {
    if (!this.pending) return;
    this.repaint();
    const c = this.ctx;
    const pts = this.pending.pts;
    c.save();
    c.strokeStyle = this.pending.col;
    c.lineWidth = Math.max(this.size, 1);
    c.beginPath();
    c.moveTo(pts[0].x, pts[0].y);
    for (const q of pts.slice(1)) c.lineTo(q.x, q.y);
    c.stroke();
    c.restore();
  }

  commitPending() {
    const pend = this.pending;
    this.pending = null;
    if (!pend || pend.pts.length < 2) return;
    this.beginOp();
    const c = this.cctx;
    const pts = pend.pts;
    c.save();
    c.strokeStyle = pend.col;
    c.fillStyle = this.color.bg;
    c.lineWidth = Math.max(this.size, 1);
    c.lineJoin = 'round';
    c.beginPath();
    c.moveTo(pts[0].x, pts[0].y);
    if (pend.tool === 'curve') {
      if (pts.length === 4) c.bezierCurveTo(pts[2].x, pts[2].y, pts[3].x, pts[3].y, pts[1].x, pts[1].y);
      else if (pts.length === 3) c.quadraticCurveTo(pts[2].x, pts[2].y, pts[1].x, pts[1].y);
      else c.lineTo(pts[1].x, pts[1].y);
      c.stroke();
    } else {
      for (const q of pts.slice(1)) c.lineTo(q.x, q.y);
      c.closePath();
      if (this.fillMode !== 'outline') c.fill();
      if (this.fillMode !== 'filled') c.stroke();
    }
    c.restore();
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const q of pts) { x0 = Math.min(x0, q.x); y0 = Math.min(y0, q.y); x1 = Math.max(x1, q.x); y1 = Math.max(y1, q.y); }
    this.markDirty(x0, y0, x1, y1, this.size + 4);
    this.endOp({ handDrawn: true });
    this.repaint();
  }

  /* ------------------------------------------------------------------ text */
  openTextDialog() {
    $('pt-textinput').value = '';
    $('pt-textsize').value = String(this.textSize);
    show($('pt-textdlg'));
    setTimeout(() => $('pt-textinput').focus(), 50);
  }

  commitText() {
    const val = $('pt-textinput').value;
    this.textSize = Number($('pt-textsize').value) || 32;
    hide($('pt-textdlg'));
    if (!val || !this.pendingText) return;
    const { x, y } = this.pendingText;
    this.beginOp();
    const c = this.cctx;
    c.save();
    c.fillStyle = this.color.fg;
    c.font = `${this.textSize}px Tahoma, sans-serif`;
    c.textBaseline = 'top';
    c.fillText(val, x, y);
    const m = c.measureText(val);
    c.restore();
    this.markDirty(x, y, x + m.width, y + this.textSize * 1.4, 4);
    this.endOp({ handDrawn: true });
    this.repaint();
  }

  /* ----------------------------------------------------------------- photo */
  async openPhoto(file) {
    const url = URL.createObjectURL(file);
    try {
      const img = await loadImage(url);
      // cap the working size: keeps tracing fast and undo patches small
      const MAX = 1000;
      const scale = Math.min(MAX / img.naturalWidth, MAX / img.naturalHeight, 1);
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));

      const src = document.createElement('canvas');
      src.width = w; src.height = h;
      const sctx = src.getContext('2d', { willReadFrequently: true });
      sctx.drawImage(img, 0, 0, w, h);
      this.srcImageData = sctx.getImageData(0, 0, w, h);
      this.photoName = file.name.replace(/\.[^.]+$/, '');
      await this.runTrace();
    } catch (err) {
      hide($('pt-busy'));
      this.message('Paint cannot read that file.', 'warn');
      console.error(err);
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  async runTrace() {
    const imageData = this.srcImageData;
    if (!imageData) return;
    const { width: w, height: h } = imageData;

    hide($('pt-open'));
    show($('pt-busy'));
    setProgress(0);

    const t0 = performance.now();
    const trace = await tracePhoto(imageData, {
      detail: this.detail,
      onProgress: (p, msg) => { setProgress(p); $('pt-busy-msg').textContent = msg; },
    });
    const ms = Math.round(performance.now() - t0);

    this.newCanvas(w, h);
    this.trace = trace;

    // paint the ink layer: black where ink, transparent elsewhere
    const inkImg = this.ictx.createImageData(w, h);
    const d = inkImg.data;
    for (let i = 0; i < w * h; i++) {
      if (trace.ink[i]) { d[i * 4] = 20; d[i * 4 + 1] = 20; d[i * 4 + 2] = 20; d[i * 4 + 3] = 255; }
    }
    this.ictx.putImageData(inkImg, 0, 0);

    this.repaint();
    hide($('pt-busy'));
    this.setTool('fill');
    $('pt-title').textContent = `${this.photoName || 'untitled'} - Paint`;
    this.setStatus('msg', `${trace.regionCount} areas to colour — tap one with the bucket. (traced in ${ms}ms)`);
    if (window.__PT_HOOK) window.__PT_HOOK({ regionCount: trace.regionCount, ms, w, h });
  }

  async setDetail(d) {
    if (!this.srcImageData) { this.message('Open a photo first.'); return; }
    this.detail = d;
    await this.runTrace();
  }

  autoColour() {
    if (!this.trace) { this.message('Open a photo first.'); return; }
    const { labels, means } = this.trace;
    this.beginOp();
    const img = this.cctx.getImageData(0, 0, this.w, this.h);
    const d = img.data;
    for (let i = 0; i < labels.length; i++) {
      const r = labels[i];
      d[i * 4] = means[r * 3];
      d[i * 4 + 1] = means[r * 3 + 1];
      d[i * 4 + 2] = means[r * 3 + 2];
      d[i * 4 + 3] = 255;
    }
    this.cctx.putImageData(img, 0, 0);
    this.markDirty(0, 0, this.w, this.h);
    this.endOp();
    this.repaint();
  }

  /* ------------------------------------------------------------------ save */
  save() {
    this.canvas.toBlob((blob) => {
      if (!blob) { this.message('Could not save the picture.', 'warn'); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'anemoia-paint.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }, 'image/png');
  }

  /* ----------------------------------------------------------- image edits */
  transform(kind) {
    const tmp = document.createElement('canvas');
    tmp.width = this.w; tmp.height = this.h;
    tmp.getContext('2d').drawImage(this.canvas, 0, 0);

    const rotate = kind === 'rot90' || kind === 'rot270';
    const nw = rotate ? this.h : this.w;
    const nh = rotate ? this.w : this.h;

    // flattening is the honest outcome: the ink layer cannot survive a rotate
    // as a separate layer without also rotating the region map
    const flatten = () => {
      this.trace = null;
      this.ictx.clearRect(0, 0, this.w, this.h);
    };

    this.newCanvas(nw, nh);
    flatten();
    const c = this.cctx;
    c.save();
    if (kind === 'fliph') { c.translate(nw, 0); c.scale(-1, 1); }
    else if (kind === 'flipv') { c.translate(0, nh); c.scale(1, -1); }
    else if (kind === 'rot90') { c.translate(nw, 0); c.rotate(Math.PI / 2); }
    else if (kind === 'rot270') { c.translate(0, nh); c.rotate(-Math.PI / 2); }
    else if (kind === 'rot180') { c.translate(nw, nh); c.rotate(Math.PI); }
    c.drawImage(tmp, 0, 0);
    c.restore();
    this.repaint();
  }

  invertColours() {
    this.beginOp();
    const img = this.cctx.getImageData(0, 0, this.w, this.h);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      d[i] = 255 - d[i]; d[i + 1] = 255 - d[i + 1]; d[i + 2] = 255 - d[i + 2];
    }
    this.cctx.putImageData(img, 0, 0);
    this.markDirty(0, 0, this.w, this.h);
    this.endOp();
    this.repaint();
  }

  clearImage() {
    this.beginOp();
    this.cctx.fillStyle = this.color.bg;
    this.cctx.fillRect(0, 0, this.w, this.h);
    this.markDirty(0, 0, this.w, this.h);
    this.endOp();
    this.repaint();
  }

  /* ----------------------------------------------------------------- menus */
  menuModel() {
    return {
      file: [
        { label: 'New', key: 'Ctrl+N', act: () => this.confirmNew() },
        { label: 'Open Photo…', key: 'Ctrl+O', act: () => show($('pt-open')) },
        { sep: true },
        { label: 'Save As…', key: 'Ctrl+S', act: () => this.save() },
        { sep: true },
        { label: 'I’m done, claim reward', act: () => this.openReward(), off: () => !this.hasArt() },
      ],
      edit: [
        { label: 'Undo', key: 'Ctrl+Z', act: () => this.undo(), off: () => !this.undoStack.length },
        { label: 'Repeat', key: 'Ctrl+Y', act: () => this.redo(), off: () => !this.redoStack.length },
      ],
      image: [
        { label: 'Flip Horizontal', act: () => this.transform('fliph') },
        { label: 'Flip Vertical', act: () => this.transform('flipv') },
        { sep: true },
        { label: 'Rotate 90°', act: () => this.transform('rot90') },
        { label: 'Rotate 180°', act: () => this.transform('rot180') },
        { label: 'Rotate 270°', act: () => this.transform('rot270') },
        { sep: true },
        { label: 'Invert Colours', key: 'Ctrl+I', act: () => this.invertColours() },
        { label: 'Clear Image', key: 'Ctrl+Shift+N', act: () => this.clearImage() },
        { sep: true },
        // Re-tracing rebuilds the region map, so it necessarily discards colouring.
        { label: 'Redraw — Simple', act: () => this.setDetail(0.2), off: () => !this.srcImageData },
        { label: 'Redraw — Medium', act: () => this.setDetail(0.55), off: () => !this.srcImageData },
        { label: 'Redraw — Detailed', act: () => this.setDetail(0.85), off: () => !this.srcImageData },
      ],
      colors: [
        { label: 'Colour It In For Me', act: () => this.autoColour(), off: () => !this.trace },
      ],
      help: [
        { label: 'How to use', act: () => this.message(
          'Tap a colour, then tap an area with the bucket to fill it.\n\n' +
          'Two fingers to zoom and pan. Pick another tool to draw on top.\n\n' +
          'File > Save As downloads your picture.') },
        { sep: true },
        { label: 'About Paint', act: () => this.message('ANEMOIA Paint\n*a canvas for nostalgia;') },
      ],
    };
  }

  bindMenus() {
    this.menus = this.menuModel();
    this.layer = $('pt-menulayer');
    for (const btn of $('pt-menubar').querySelectorAll('.pt-menu')) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.openMenu(btn);
      });
    }
    /*
     * Close only on a press against the BACKDROP. The dropdown lives inside
     * this layer, so an unconditional handler here fires on the way up from a
     * menu item, wipes innerHTML, and removes the button before `click` can
     * land — which reads as "the File menu does nothing".
     */
    this.layer.addEventListener('pointerdown', (e) => {
      if (e.target === this.layer) this.closeMenu();
    });
    document.addEventListener('keydown', (e) => this.onKey(e));
  }

  openMenu(btn) {
    const name = btn.dataset.menu;
    if (!name || !this.menus[name]) return;
    if (this.openMenuName === name) { this.closeMenu(); return; }
    this.closeMenu();
    this.openMenuName = name;
    btn.classList.add('is-open');

    const dd = document.createElement('div');
    dd.className = 'pt-dropdown';
    for (const item of this.menus[name]) {
      if (item.sep) {
        const s = document.createElement('div');
        s.className = 'pt-sep';
        dd.appendChild(s);
        continue;
      }
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pt-mi';
      b.innerHTML = `<span>${item.label}</span>${item.key ? `<span class="pt-mi__key">${item.key}</span>` : ''}`;
      if (item.off && item.off()) b.disabled = true;
      b.addEventListener('click', () => { this.closeMenu(); item.act(); });
      dd.appendChild(b);
    }
    const r = btn.getBoundingClientRect();
    dd.style.left = `${Math.min(r.left, window.innerWidth - 240)}px`;
    dd.style.top = `${r.bottom}px`;
    this.layer.appendChild(dd);
    this.layer.hidden = false;
  }

  closeMenu() {
    this.openMenuName = null;
    this.layer.hidden = true;
    this.layer.innerHTML = '';
    for (const b of $('pt-menubar').querySelectorAll('.pt-menu')) b.classList.remove('is-open');
  }

  onKey(e) {
    if (e.target.tagName === 'INPUT') return;
    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && e.key.toLowerCase() === 'z') { e.preventDefault(); e.shiftKey ? this.redo() : this.undo(); }
    else if (ctrl && e.key.toLowerCase() === 'y') { e.preventDefault(); this.redo(); }
    else if (ctrl && e.key.toLowerCase() === 's') { e.preventDefault(); this.save(); }
    else if (ctrl && e.key.toLowerCase() === 'o') { e.preventDefault(); show($('pt-open')); }
    else if (e.key === 'Escape') { this.closeMenu(); this.commitPending(); }
  }

  /* --------------------------------------------------------------- dialogs */
  bindDialogs() {
    const fileInput = $('pt-file');
    fileInput.addEventListener('change', () => {
      const f = fileInput.files && fileInput.files[0];
      if (f) this.openPhoto(f);
      fileInput.value = '';
    });

    const drop = $('pt-drop');
    for (const ev of ['dragenter', 'dragover']) {
      drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-over'); });
    }
    for (const ev of ['dragleave', 'drop']) {
      drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('is-over'); });
    }
    drop.addEventListener('drop', (e) => {
      const f = e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) this.openPhoto(f);
    });

    // Welcome -> blank canvas. This is the default entry: a drawing surface,
    // not a file picker.
    $('pt-start').addEventListener('click', () => {
      hide($('pt-welcome'));
      this.startBlank();
    });

    $('pt-open-cancel').addEventListener('click', () => hide($('pt-open')));

    $('pt-msg-ok').addEventListener('click', () => {
      hide($('pt-msg'));
      if (this.msgResolve) { this.msgResolve(true); this.msgResolve = null; }
    });
    $('pt-msg-cancel').addEventListener('click', () => {
      hide($('pt-msg'));
      if (this.msgResolve) { this.msgResolve(false); this.msgResolve = null; }
    });

    $('pt-text-ok').addEventListener('click', () => this.commitText());
    $('pt-text-cancel').addEventListener('click', () => hide($('pt-textdlg')));
  }

  fitSize() {
    const r = this.wrap.getBoundingClientRect();
    return {
      w: Math.max(320, Math.min(1000, Math.round(r.width - 24))),
      h: Math.max(240, Math.min(1000, Math.round(r.height - 24))),
    };
  }

  message(text, kind = 'info') {
    $('pt-msg-text').textContent = text;
    $('pt-msg-text').style.whiteSpace = 'pre-line';
    const icon = $('pt-msg-icon');
    icon.textContent = kind === 'warn' ? '!' : 'i';
    icon.classList.toggle('is-warn', kind === 'warn');
    $('pt-msg-cancel').hidden = true;
    show($('pt-msg'));
  }

  confirmNew() {
    $('pt-msg-text').textContent = 'Start a new picture? Your current one will be lost.';
    $('pt-msg-cancel').hidden = false;
    show($('pt-msg'));
    this.msgResolve = (ok) => {
      if (!ok) return;
      this.startBlank();
    };
  }

  startBlank() {
    this.trace = null;
    this.srcImageData = null;
    const { w, h } = this.fitSize();
    this.newCanvas(w, h);
    this.setTool('pencil');
    $('pt-title').textContent = 'untitled - Paint';
  }

}

/* ------------------------------------------------------------------ utils */
function show(el) { el.hidden = false; }
function hide(el) { el.hidden = true; }
function setProgress(p) { $('pt-progress-bar').style.width = `${Math.round(p * 100)}%`; }

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

function loadImage(url) {
  return new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = url;
  });
}

/* ------------------------------------------------------------------- boot */
const app = new Paint();
window.__PAINT = app;   // headless verification hook; harmless in production
